import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../data/schemas';

type Service = {
  title: string;
  description: string;
  highlights: string[];
  cta: string;
};

const services: Service[] = [
  {
    title: 'Event Makeup',
    description:
      'Polished, camera-ready looks for galas, premieres, and high-visibility appearances.',
    highlights: ['Rapid touch-up support', 'Custom colour palettes', 'On-location set-ups'],
    cta: 'Plan your event glam',
  },
  {
    title: 'Weddings',
    description:
      'Elevated beauty experiences curated for brides, wedding parties, and VIP guests.',
    highlights: ['Bridal previews', 'Day-of timeline management', 'Artist team add-ons'],
    cta: 'Design your bridal look',
  },
  {
    title: 'One-on-One Lessons',
    description:
      'Personal coaching tailored to your features, lifestyle, and beauty goals.',
    highlights: ['Technique breakdowns', 'Signature product lineup', 'Lookbook to take home'],
    cta: 'Book a private session',
  },
  {
    title: 'Editorial & Runway',
    description:
      'Bold, trend-forward artistry for campaigns, shoots, and backstage environments.',
    highlights: ['Moodboard collaboration', 'Full creative direction', 'On-call artistry team'],
    cta: 'Request editorial support',
  },
  {
    title: 'Corporate & Brand',
    description:
      'Executive-ready looks and branded glam for internal events, panels, and media days.',
    highlights: ['Uniform brand styling', 'Group scheduling', 'Touch-up lounge options'],
    cta: 'Elevate your brand moment',
  },
  {
    title: 'Luxury Masterclasses',
    description:
      'Group experiences that blend education, indulgence, and hands-on artistry.',
    highlights: ['Curated kits', 'Live demonstrations', 'VIP gifting upgrades'],
    cta: 'Host a masterclass',
  },
];

export default function Products() {
  const servicesSchema = breadcrumbSchema([
    { name: 'Home', url: 'https://letsfaceitcosmetics.com' },
    { name: 'Services', url: 'https://letsfaceitcosmetics.com/services' }
  ]);

  return (
    <>
      <SEO
        title="Services - Luxury Makeup Artistry in Atlanta"
        description="Professional makeup services in Atlanta including bridal makeup, wedding glam, editorial looks, event makeup, one-on-one lessons, and luxury masterclasses. Tailored artistry for every occasion."
        keywords="bridal makeup services Atlanta, wedding makeup artist Atlanta, event makeup Atlanta, editorial makeup Atlanta, makeup lessons Atlanta, luxury masterclass, corporate makeup, runway makeup Atlanta"
        schema={servicesSchema}
      />
      <section
        className="fixed top-0 left-0 w-full h-dvh bg-linear-to-b from-[#f1ede7] via-[#ece6de]
      to-[#e4ded4] text-[#161616] pt-20 md:pt-[150px] pb-px xsm:pb-[11px] sm:pb-[14px] md:pb-[42px] lg:pb-[30px] overflow-y-auto"
      >
      {/* Normal flow - content starts at top and scrolls if needed */}
      <div className="relative mx-auto flex w-full flex-col 
      px-4 text-left sm:px-8 py-16 xsm:py-13 xsm2:py-[62px] sm:py-18 md:py-[34px] lg:py-[30px] md:px-12 lg:px-20">
        <header className="max-w-4xl -mt-7 xsm:-mt-3 sm:-mt-px lg:mt-0">
          <span className="uppercase tracking-[0.35em] text-sm text-[#5b5141] ">
            Signature Services
          </span>
          <h1 className="mt-4 lg:mt-4 font-mak text-4xl leading-10 xsm:leading-tight text-[#1b1b1b] md:text-[46px]">
            Tailored artistry for every occasion.
          </h1>
          <p className="mt-3 md:mt-4 lg:mt-1 text-[18px] text-[#3c3c3c] md:text-[20px]">
            Choose the experience that matches your moment. Each option can be customised with additional artists,
            onsite touch-ups, and premium product wardrobes.
          </p>
        </header>

        <div className="mt-5 grid gap-6 sm:mt-10 sm:grid-cols-2 lg:mt-5 xl:grid-cols-3 mb-0 lg:mb-6">
          {services.map((service) => (
            <article
              key={service.title}
              className="flex flex-col border border-[#cbbda9] bg-[#111111]/95 p-6 lg:p-12 text-white 
              shadow-[0_30px_80px_-50px_rgba(17,17,17,0.9)] backdrop-blur transition duration-200 hover:border-[#e6d9c6] hover:bg-[#151515]"
            >
              <div className="flex-1">
                <h2 className="font-mak lg:-mt-5 text-2xl lg:text-4xl text-white">{service.title}</h2>
                <p className="mt-3 lg:mt-5 text-sm lg:text-base text-white/70">{service.description}</p>
                <ul className="mt-4 lg:mt-6 space-y-2 text-sm text-white/60">
                  {service.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 inline-block h-2 w-2 shrink-0 bg-[#cbbda9]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/book"
                className="mt-6 lg:mt-12 inline-flex items-center justify-center border border-[#cbbda9]/40 bg-transparent px-4 lg:px-6
                py-2 lg:py-4 text-xs uppercase tracking-[0.25em] text-[#d9cbb8] transition-all duration-300 hover:bg-[#cbbda9] 
                hover:border-[#cbbda9] hover:text-[#1f1b16]"
              >
                {service.cta}
              </Link>
            </article>
          ))}
        </div>
      </div>
      </section>
    </>
  );
}